import { useTranslatedOptionEnum } from '@applyonline/shared'
import { EHomeStatus, getEHomeStatusList } from '@if/core/types'
import { useCallback, useEffect, useState } from 'react'
import { Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { HomeInformationInput } from '../../../types/Schemas/CreditApplicationSchema'

interface HousingSectionProps {
  register: UseFormRegister<HomeInformationInput>
  errors: FieldErrors<HomeInformationInput>
  watch: UseFormWatch<HomeInformationInput>
  setValue: UseFormSetValue<HomeInformationInput>
  control: Control<HomeInformationInput>
  spending: NonNullable<HomeInformationInput['spending']>
}

const HousingSection = ({ register, errors, watch, setValue, control, spending }: HousingSectionProps) => {
  const {
    t,
    i18n: { resolvedLanguage: lang },
  } = useTranslation()

  const homeStatusOptions = useTranslatedOptionEnum('EHomeStatus', getEHomeStatusList(), 'enum')

  const totalMonthlyHomePayment = watch('spending.totalMonthlyHomePayment')
  const homeFeeTypeId = useWatch({
    control,
    name: 'spending.homeFeeTypeId',
    defaultValue: spending.homeFeeTypeId,
  })

  const needTotalMonthlyHomePayment = useCallback(() => {
    return homeFeeTypeId !== EHomeStatus.Owner && homeFeeTypeId !== EHomeStatus.WithRelative
  }, [homeFeeTypeId])

  const needMonthlyHomePayment = () => {
    return (
      (totalMonthlyHomePayment !== null && totalMonthlyHomePayment !== undefined && totalMonthlyHomePayment > 0) ||
      homeFeeTypeId === EHomeStatus.WithRelative
    )
  }

  const [previousHomeFeeTypeId, setPreviousHomeFeeTypeId] = useState<EHomeStatus | undefined>(homeFeeTypeId)
  useEffect(() => {
    if (homeFeeTypeId !== previousHomeFeeTypeId) {
      setValue('spending.totalMonthlyHomePayment', null, {
        shouldTouch: true,
        shouldDirty: true,
      })
      setValue('spending.housingMonthly', null, {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [homeFeeTypeId, setValue, previousHomeFeeTypeId, needTotalMonthlyHomePayment])
  useEffect(() => {
    setPreviousHomeFeeTypeId(homeFeeTypeId)
  }, [setPreviousHomeFeeTypeId, homeFeeTypeId])

  return (
    <>
      <div className="control-group" style={{ paddingBottom: 0 }}>
        <label htmlFor="housing_status" style={{ marginBottom: 0 }}>
          {t('housing.currentStatus')}
          <span className="help-tip">
            <p>{t('livingAtAddressSince.details')}</p>
          </span>
        </label>
      </div>

      <div className="control-group mid">
        <label htmlFor="housing_status">{t('housing.homeFeeType')}</label>
        <div className={`dropdown-wrap ${errors.spending?.homeFeeTypeId && 'error'}`}>
          <select {...register('spending.homeFeeTypeId')} id="homeFeeTypeId">
            {homeStatusOptions}
          </select>
        </div>
      </div>

      {needTotalMonthlyHomePayment() && (
        <div className={`control-group mid  ${errors.spending?.totalMonthlyHomePayment && 'error'}`}>
          <label htmlFor="mortgage_amount">
            {t('housing.totalMonthlyPayment', { homeFeeType: homeFeeTypeId ? t(`housing.${homeFeeTypeId}`) : '' })}
          </label>
          <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
            <input type="number" id="totalMonthlyHomePayment" {...register('spending.totalMonthlyHomePayment')} />
          </div>
        </div>
      )}

      {needMonthlyHomePayment() && (
        <div className={`control-group  ${errors.spending?.housingMonthly && 'error'}`}>
          <label htmlFor="mortgage_payment">
            {t('housing.contributedMonthlyPayment', {
              homeFeeType: homeFeeTypeId ? t(`housing.${homeFeeTypeId}`) : '',
            })}
          </label>
          <div className={`number-wrap ${lang === 'en' ? 'startwith' : 'endwith'}`}>
            <input type="number" id="monthlyHomePayment" {...register('spending.housingMonthly')} />
          </div>
        </div>
      )}
    </>
  )
}

export default HousingSection
